import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { axios } from '@/library/axios';
import type { BaseResponse } from '@/types';

import { useFeatureFlags } from '../feature-flags';
import type { LoanSchemeType } from './types';

const getLoanScheme = async (coords: LocationCoords | undefined) => {
  return axios<LoanSchemeType[], BaseResponse<LoanSchemeType[]>, void>({
    url: '/glfh/scheme-selection',
    method: 'get',
    params: {
      ...coords,
    },
  });
};

type LocationCoords = {
  latitude: number;
  longitude: number;
};

export const useLoanScheme = () => {
  const [loanSchemes, setLoanSchemes] = useState<LoanSchemeType[]>([]);
  const [locationCoords, setLocationCoords] = useState<LocationCoords>();

  const { featureFlags } = useFeatureFlags();

  useEffect(() => {
    const onLocationSuccess = (position: GeolocationPosition) => {
      const crd = position.coords;
      setLocationCoords({
        latitude: crd.latitude,
        longitude: crd.longitude,
      });
    };

    if (featureFlags.release_gl_location_schemes) {
      navigator.geolocation.getCurrentPosition(onLocationSuccess);
    }
  }, [featureFlags]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['loan-schemes', locationCoords],
    queryFn: () => getLoanScheme(locationCoords),
    useErrorBoundary: false,
    refetchOnWindowFocus: true,
    retry: 3,
  });

  useEffect(() => {
    const resultData = data?.data?.data;
    const newLoanScheme = Array.isArray(resultData) ? resultData : [];
    newLoanScheme.sort(
      (scheme1, scheme2) => scheme1.baseRate - scheme2.baseRate
    );
    setLoanSchemes(newLoanScheme);
  }, [data]);

  return {
    loanSchemes,
    isLoading,
    refetch,
  };
};
